import React from 'react';
import { Layout, Menu, Drawer, Button, Avatar, Dropdown, Tooltip, Skeleton } from 'antd';
import { PanelLeftClose, PanelLeftOpen, LogOut, User as UserIcon, ChevronDown, Search, Menu as MenuIcon, ChevronRight } from 'lucide-react';
import BrandMark from '../BrandMark';
import { layout } from '../../../theme/tokens';
import CommandPalette from './CommandPalette';

const { Sider, Header } = Layout;

/* ---------- small view pieces ---------- */

function UserAvatar({ shell, size = 32 }) {
    return (
        <Avatar size={size} className="!bg-primary-100 !text-primary-700 !font-semibold shrink-0" icon={!shell.initials && <UserIcon />}>
            {shell.initials}
        </Avatar>
    );
}

function SearchTrigger({ shell }) {
    if (shell.collapsed) {
        return (
            <Tooltip title="Buscar (⌘K)" placement="right">
                <button
                    type="button"
                    onClick={shell.openSearch}
                    className="mx-auto flex w-10 h-9 items-center justify-center rounded-lg text-surface-500 hover:bg-surface-100 hover:text-surface-900 transition-colors">
                    <Search />
                </button>
            </Tooltip>
        );
    }
    return (
        <button
            type="button"
            onClick={shell.openSearch}
            className="mx-3 flex h-9 items-center gap-2.5 rounded-lg border border-surface-200 bg-surface-50 px-3 text-left text-sm text-surface-500 hover:border-surface-300 hover:bg-white transition-colors"
            style={{ width: `calc(100% - 24px)` }}>
            <Search className="text-surface-400" />
            <span className="flex-1">Buscar…</span>
            <kbd className="kbd">⌘K</kbd>
        </button>
    );
}

function NavMenu({ shell, inDrawer = false }) {
    if (shell.loading) {
        return (
            <div className="px-3 py-2 space-y-2">
                {[...Array(6)].map((_, i) => (
                    <Skeleton.Button key={i} active block style={{ height: 32, borderRadius: 8, opacity: 1 - i * 0.12 }} />
                ))}
            </div>
        );
    }
    return (
        <Menu
            mode="inline"
            className="shelf-nav"
            selectedKeys={[shell.current]}
            defaultOpenKeys={shell.defaultOpenKeys}
            inlineCollapsed={inDrawer ? false : shell.collapsed}
            items={shell.menuItems}
            onClick={shell.onMenuClick}
        />
    );
}

function UserFooter({ shell }) {
    if (shell.loading) return null;
    if (shell.collapsed) {
        return (
            <div className="flex flex-col items-center gap-1 py-3 border-t border-surface-100">
                <Tooltip title={`${shell.name} · ${shell.roleLabel}`} placement="right">
                    <span>
                        <UserAvatar shell={shell} />
                    </span>
                </Tooltip>
                <Tooltip title="Cerrar sesión" placement="right">
                    <button
                        type="button"
                        onClick={shell.logout}
                        className="flex w-10 h-9 items-center justify-center rounded-lg text-surface-500 hover:bg-danger-bg hover:text-danger-fg transition-colors">
                        <LogOut />
                    </button>
                </Tooltip>
            </div>
        );
    }
    return (
        <div className="flex items-center gap-3 px-4 py-3 border-t border-surface-100">
            <UserAvatar shell={shell} />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-surface-900 truncate">{shell.name}</div>
                <div className="text-xs text-surface-500 truncate">{shell.roleLabel}</div>
            </div>
            <Tooltip title="Cerrar sesión">
                <button
                    type="button"
                    onClick={shell.logout}
                    className="flex w-8 h-8 items-center justify-center rounded-lg text-surface-400 hover:bg-danger-bg hover:text-danger-fg transition-colors">
                    <LogOut />
                </button>
            </Tooltip>
        </div>
    );
}

function SidebarBody({ shell, inDrawer = false }) {
    const collapsed = inDrawer ? false : shell.collapsed;
    return (
        <div className="flex flex-col h-full bg-white">
            <div className={`flex items-center h-[60px] shrink-0 ${collapsed ? 'justify-center' : 'gap-2.5 px-4'}`}>
                <BrandMark />
                {!collapsed && (
                    <>
                        <div className="flex-1 min-w-0 leading-tight">
                            <div className="text-[15px] font-semibold text-surface-900 tracking-tight">Shelf</div>
                            {/* The company this session works in (company/session.js) */}
                            <div className="text-[11px] text-surface-500 truncate" title={`Panel administrativo · ${shell.workspaceLabel}`}>
                                {shell.workspaceLabel || 'Panel administrativo'}
                            </div>
                        </div>
                        {!inDrawer && (
                            <Tooltip title="Colapsar menú">
                                <button
                                    type="button"
                                    onClick={shell.toggleCollapsed}
                                    className="flex w-8 h-8 items-center justify-center rounded-lg text-surface-400 hover:bg-surface-100 hover:text-surface-700 transition-colors">
                                    <PanelLeftClose />
                                </button>
                            </Tooltip>
                        )}
                    </>
                )}
            </div>

            <div className="pb-3">
                <SearchTrigger shell={{ ...shell, collapsed }} />
            </div>

            <nav className="flex-1 overflow-y-auto overflow-x-hidden pb-2">
                <NavMenu shell={shell} inDrawer={inDrawer} />
            </nav>

            {collapsed && !inDrawer && (
                <div className="flex justify-center pb-1">
                    <Tooltip title="Expandir menú" placement="right">
                        <button
                            type="button"
                            onClick={shell.toggleCollapsed}
                            className="flex w-10 h-9 items-center justify-center rounded-lg text-surface-400 hover:bg-surface-100 hover:text-surface-700 transition-colors">
                            <PanelLeftOpen />
                        </button>
                    </Tooltip>
                </div>
            )}

            <UserFooter shell={{ ...shell, collapsed }} />
        </div>
    );
}

/* ---------- shell views ---------- */

/** Desktop sidebar + mobile header/drawer + command palette. `shell` = useSidebarController(). */
export default function Sidebar({ shell }) {
    return (
        <>
            <CommandPalette
                open={shell.searchOpen}
                onClose={shell.closeSearch}
                navigate={shell.navigate}
                userType={shell.userType}
                clientId={shell.clientId}
            />

            <Sider
                collapsible
                collapsed={shell.collapsed}
                onCollapse={shell.setCollapsed}
                trigger={null}
                width={layout.sidebarWidth}
                collapsedWidth={layout.sidebarCollapsedWidth}
                theme="light"
                className="hidden md:block border-r border-surface-200"
                style={{ height: '100vh', position: 'sticky', top: 0, left: 0, overflow: 'hidden', background: '#fff' }}>
                <SidebarBody shell={shell} />
            </Sider>

            {/* Mobile header */}
            <div className="md:hidden fixed top-0 inset-x-0 z-50 h-14 flex items-center justify-between px-4 bg-white/90 backdrop-blur border-b border-surface-200">
                <div className="flex items-center gap-2.5">
                    <BrandMark size={30} />
                    <span className="leading-tight">
                        <span className="block text-[15px] font-semibold text-surface-900">Shelf</span>
                        {shell.workspaceLabel && <span className="block text-[11px] text-surface-500">{shell.workspaceLabel}</span>}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <Button type="text" icon={<Search />} onClick={shell.openSearch} aria-label="Buscar" />
                    <Button type="text" icon={<MenuIcon />} onClick={shell.openMobile} aria-label="Menú" />
                </div>
            </div>

            <Drawer
                placement="left"
                onClose={shell.closeMobile}
                open={shell.mobileOpen}
                width={280}
                closable={false}
                styles={{ body: { padding: 0 } }}>
                <SidebarBody shell={shell} inDrawer />
            </Drawer>

            <div className="md:hidden h-14" />
        </>
    );
}

/** Desktop top bar: where you are + account menu. */
export function TopBar({ shell }) {
    const userMenuItems = [
        { key: 'profile', icon: <UserIcon />, label: 'Mi Perfil' },
        { type: 'divider' },
        { key: 'logout', icon: <LogOut />, label: 'Cerrar Sesión', danger: true, onClick: shell.logout }
    ];

    return (
        <Header
            className="hidden md:flex items-center justify-between !px-8 bg-surface-50/80 backdrop-blur border-b border-transparent"
            style={{ height: layout.topBarHeight, lineHeight: 'normal', position: 'sticky', top: 0, zIndex: 20 }}>
            <nav className="flex items-center gap-1.5 text-[13px] min-w-0">
                {shell.trail.map((label, i) => (
                    <React.Fragment key={label}>
                        {i > 0 && <ChevronRight className="text-[12px] text-surface-300" />}
                        <span className={i === shell.trail.length - 1 ? 'font-medium text-surface-900' : 'text-surface-500'}>{label}</span>
                    </React.Fragment>
                ))}
            </nav>

            <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
                <button
                    type="button"
                    className="flex items-center gap-2.5 h-10 pl-1.5 pr-3 rounded-full border border-surface-200 bg-white hover:border-surface-300 transition-colors">
                    <UserAvatar shell={shell} size={28} />
                    <span className="text-left leading-tight">
                        <span className="block text-[13px] font-medium text-surface-900">{shell.name}</span>
                        <span className="block text-[11px] text-surface-500">{shell.roleLabel}</span>
                    </span>
                    <ChevronDown className="text-[13px] text-surface-400" />
                </button>
            </Dropdown>
        </Header>
    );
}
